const QUEUE_KEY = 'offline_message_queue';
const FAILED_KEY = 'failed_message_queue';

export const OfflineQueueService = {
    // Add message to queue
    addToQueue: (message) => {
        try {
            const queue = OfflineQueueService.getQueue();
            queue.push({
                ...message,
                queuedAt: Date.now(),
                tempId: message.tempId || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            });
            localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
            return true;
        } catch (error) {
            console.error("Error adding to offline queue:", error);
            return false;
        }
    },

    // Get all queued messages
    getQueue: () => {
        try {
            const queue = localStorage.getItem(QUEUE_KEY);
            return queue ? JSON.parse(queue) : [];
        } catch (error) {
            console.error("Error getting offline queue:", error);
            return [];
        }
    },

    // Remove message from queue
    removeFromQueue: (tempId) => {
        try {
            const queue = OfflineQueueService.getQueue();
            const newQueue = queue.filter(msg => msg.tempId !== tempId);
            localStorage.setItem(QUEUE_KEY, JSON.stringify(newQueue));
        } catch (error) {
            console.error("Error removing from offline queue:", error);
        }
    },

    // Clear queue
    clearQueue: () => {
        try {
            localStorage.removeItem(QUEUE_KEY);
        } catch (error) {
            console.error("Error clearing offline queue:", error);
        }
    },

    /**
     * Store a failed message to the failed message queue.
     * @param {string} chatId
     * @param {Object} messageData - Must include clientMessageId
     * @param {string|Error} error - The error that caused the failure
     */
    addFailedMessage: (chatId, messageData, error) => {
        try {
            const failed = OfflineQueueService.getFailedMessages();
            failed.push({
                chatId,
                messageData,
                error: error instanceof Error ? error.message : String(error),
                failedAt: Date.now(),
                retryCount: 0
            });
            localStorage.setItem(FAILED_KEY, JSON.stringify(failed));
        } catch (err) {
            console.error("Error adding failed message:", err);
        }
    },

    /**
     * Get all failed messages.
     * @returns {Array}
     */
    getFailedMessages: () => {
        try {
            const data = localStorage.getItem(FAILED_KEY);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error("Error getting failed messages:", error);
            return [];
        }
    },

    /**
     * Get failed messages filtered by chatId.
     * @param {string} chatId
     * @returns {Array}
     */
    getFailedMessagesForChat: (chatId) => {
        try {
            const failed = OfflineQueueService.getFailedMessages();
            return failed.filter(msg => msg.chatId === chatId);
        } catch (error) {
            console.error("Error getting failed messages for chat:", error);
            return [];
        }
    },

    /**
     * Increment the retryCount for a failed message by clientMessageId.
     * @param {string} clientMessageId
     */
    incrementRetryCount: (clientMessageId) => {
        try {
            const failed = OfflineQueueService.getFailedMessages();
            const index = failed.findIndex(
                msg => msg.messageData && msg.messageData.clientMessageId === clientMessageId
            );
            if (index !== -1) {
                failed[index].retryCount = (failed[index].retryCount || 0) + 1;
                localStorage.setItem(FAILED_KEY, JSON.stringify(failed));
            }
        } catch (error) {
            console.error("Error incrementing retry count:", error);
        }
    },

    /**
     * Remove a failed message by clientMessageId.
     * @param {string} clientMessageId
     */
    removeFailedMessage: (clientMessageId) => {
        try {
            const failed = OfflineQueueService.getFailedMessages();
            const updated = failed.filter(
                msg => !(msg.messageData && msg.messageData.clientMessageId === clientMessageId)
            );
            localStorage.setItem(FAILED_KEY, JSON.stringify(updated));
        } catch (error) {
            console.error("Error removing failed message:", error);
        }
    },

    /**
     * Clear all failed messages.
     */
    clearFailedMessages: () => {
        try {
            localStorage.removeItem(FAILED_KEY);
        } catch (error) {
            console.error("Error clearing failed messages:", error);
        }
    }
};
